/*
 * MyEssencìl — comportements globaux légers.
 * - .me-reveal : apparition au scroll (ajoute .is-visible)
 * - [data-me-count] : compteur animé jusqu'à la valeur de l'attribut
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const animateCount = (el) => {
    const target = parseFloat(el.dataset.meCount);
    if (Number.isNaN(target)) return;
    const decimals = (el.dataset.meCount.split('.')[1] || '').length;
    if (reduceMotion) {
      el.textContent = target.toFixed(decimals);
      return;
    }
    const duration = 1600;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = (target * eased).toFixed(decimals);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const el = entry.target;
              if (el.classList.contains('me-reveal')) el.classList.add('is-visible');
              if (el.hasAttribute('data-me-count')) animateCount(el);
              observer.unobserve(el);
            });
          },
          { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
        )
      : null;

  const scan = (root = document) => {
    root.querySelectorAll('.me-reveal:not(.is-visible), [data-me-count]').forEach((el) => {
      if (el.dataset.meObserved) return;
      el.dataset.meObserved = 'true';
      if (observer && !reduceMotion) observer.observe(el);
      else {
        el.classList.add('is-visible');
        if (el.hasAttribute('data-me-count')) animateCount(el);
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();

  // Éditeur de thème et rendu AJAX des sections
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  let scheduled = false;
  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      scan();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Sélecteur de langue (snippets/me-language-selector.liquid) */
if (!customElements.get('me-language-selector')) {
    customElements.define(
      'me-language-selector',
      class MeLanguageSelector extends HTMLElement {
        connectedCallback() {
          this.toggle = this.querySelector('[data-me-lang-toggle]');
          this.list = this.querySelector('.me-lang__list');
          this.form = this.querySelector('form');
          this.input = this.querySelector('[data-me-lang-input]');
          this.querySelectorAll('[data-me-lang-value]').forEach((option) => {
            option.addEventListener('click', () => this.select(option.dataset.meLangValue));
          });
          if (!this.toggle || !this.list) return;
          this.toggle.addEventListener('click', () => this.setOpen(this.list.hidden));
          this.onDocClick = (event) => {
            if (!this.contains(event.target)) this.setOpen(false);
          };
          this.onKey = (event) => {
            if (event.key === 'Escape' && !this.list.hidden) {
              this.setOpen(false);
              this.toggle.focus();
            }
          };
          document.addEventListener('click', this.onDocClick);
          document.addEventListener('keydown', this.onKey);
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocClick);
          document.removeEventListener('keydown', this.onKey);
        }

        select(code) {
          if (!this.form || !this.input) return;
          if (code === this.input.value) return this.setOpen?.(false);
          this.input.value = code;
          // Retour sur la page courante (et non sur l'URL d'un rendu de section)
          this.form.querySelectorAll('input[name="return_to"]').forEach((el) => el.remove());
          const back = document.createElement('input');
          back.type = 'hidden';
          back.name = 'return_to';
          back.value = window.location.pathname + window.location.search;
          this.form.append(back);
          this.form.submit();
        }

        setOpen(open) {
          if (!this.list || !this.toggle) return;
          this.list.hidden = !open;
          this.toggle.setAttribute('aria-expanded', String(open));
          if (open) this.list.querySelector('.is-current, button')?.focus();
        }
      }
    );
  }

/* Comparateur avant / après <me-before-after> (sections/me-before-after.liquid et me-pdp-transformation.liquid) */
if (!customElements.get('me-before-after')) {
  class MeBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('[data-me-ba-media]');
      this.handle = this.querySelector('[data-me-ba-handle]');
      this.range = this.querySelector('[data-me-ba-range]');
      if (!this.media || !this.range) return;

      this.initial = parseFloat(this.dataset.initial) || 50;
      this.dragging = false;
      this.interacted = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onInput = this.onInput.bind(this);

      this.media.addEventListener('pointerdown', this.onPointerDown);
      this.media.addEventListener('pointermove', this.onPointerMove);
      this.media.addEventListener('pointerup', this.onPointerUp);
      this.media.addEventListener('pointercancel', this.onPointerUp);
      this.range.addEventListener('input', this.onInput);

      this.setSplit(this.initial);
      this.observeReveal();
      this.setupDots();
    }

    disconnectedCallback() {
      if (this.media) {
        this.media.removeEventListener('pointerdown', this.onPointerDown);
        this.media.removeEventListener('pointermove', this.onPointerMove);
        this.media.removeEventListener('pointerup', this.onPointerUp);
        this.media.removeEventListener('pointercancel', this.onPointerUp);
      }
      if (this.range) this.range.removeEventListener('input', this.onInput);
      if (this.observer) this.observer.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.hintTimer) clearTimeout(this.hintTimer);
      if (this.track && this.onTrackScroll) this.track.removeEventListener('scroll', this.onTrackScroll);
    }

    setSplit(value) {
      const pct = Math.min(100, Math.max(0, value));
      this.style.setProperty('--me-split', `${pct.toFixed(2)}%`);
      const rounded = Math.round(pct);
      if (this.range && Number(this.range.value) !== rounded) this.range.value = String(rounded);
    }

    markInteracted() {
      this.interacted = true;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.classList.remove('is-hinting');
    }

    pctFromEvent(event) {
      const rect = this.media.getBoundingClientRect();
      return Math.min(98, Math.max(2, ((event.clientX - rect.left) / rect.width) * 100));
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (event.target.closest('a')) return;
      // Tactile : seule la poignée démarre le glissement (le reste laisse défiler le carrousel)
      const fromHandle = this.handle && this.handle.contains(event.target);
      if (!this.finePointer.matches && !fromHandle) return;
      this.dragging = true;
      this.markInteracted();
      this.classList.add('is-dragging');
      this.media.setPointerCapture(event.pointerId);
      this.setSplit(this.pctFromEvent(event));
      event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      this.setSplit(this.pctFromEvent(event));
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.classList.remove('is-dragging');
      if (this.media.hasPointerCapture(event.pointerId)) this.media.releasePointerCapture(event.pointerId);
    }

    onInput() {
      this.markInteracted();
      this.setSplit(Number(this.range.value));
    }

    animateTo(from, to, duration, done) {
      const start = performance.now();
      const step = (now) => {
        if (this.interacted) return;
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.setSplit(from + (to - from) * eased);
        if (progress < 1) this.frame = requestAnimationFrame(step);
        else if (done) done();
      };
      this.frame = requestAnimationFrame(step);
    }

    observeReveal() {
      if (this.reduceMotion || !('IntersectionObserver' in window) || window.Shopify?.designMode) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.observer.disconnect();
          setTimeout(() => {
            this.animateTo(this.initial, 22, 820, () => {
              this.animateTo(22, 50, 900, () => {
                this.classList.add('is-hinting');
                this.hintTimer = setTimeout(() => this.classList.remove('is-hinting'), 3000);
              });
            });
          }, 420);
        },
        { threshold: 0.3 }
      );
      this.observer.observe(this);
    }

    setupDots() {
      // Seule la première carte pilote les points du carrousel mobile
      this.track = this.closest('[data-me-ba-track]');
      if (!this.track || this.track.querySelector('me-before-after') !== this) return;
      const dotsWrap = this.track.parentElement.querySelector('[data-me-ba-dots]');
      if (!dotsWrap) return;
      const dots = Array.from(dotsWrap.children);
      const cards = Array.from(this.track.querySelectorAll('me-before-after'));
      let ticking = false;
      this.onTrackScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const center = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
          let active = 0;
          let min = Infinity;
          cards.forEach((card, index) => {
            const rect = card.getBoundingClientRect();
            const distance = Math.abs(rect.left + rect.width / 2 - center);
            if (distance < min) {
              min = distance;
              active = index;
            }
          });
          dots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));
        });
      };
      this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
    }
  }

  customElements.define('me-before-after', MeBeforeAfter);
}

/* Bouton Chat (Shopify Inbox) placé juste au-dessus de la barre d'achat collante (sections/sticky-add-to-cart),
   uniquement quand la barre est affichée (classe Booster « sticky-add-to-cart--visible »).
   Un seul mouvement à l'apparition / disparition de la barre (pas de suivi au défilement), avec la même durée
   d'animation que la barre ; les changements rapides sont ignorés (attente de 150 ms d'un état stable).
   Seul le bouton de lancement (.activator-nudge, dans le shadow DOM ouvert de <shopify-chat>) est déplacé. */
(() => {
  const GAP = 12;
  const SETTLE = 150;
  const STYLE_ID = 'me-chat-lift-style';
  let chat = null;
  let bar = null;
  let lift = 0;
  let timer = null;
  let watchdog = null;

  // Le bouton peut être dans un shadow DOM imbriqué (<shopify-agent-runtime>) : recherche récursive
  const findNudge = (root, depth = 0) => {
    if (!root || depth > 4) return null;
    const hit = root.querySelector('.activator-nudge');
    if (hit) return hit;
    for (const el of root.querySelectorAll('*')) {
      if (el.shadowRoot) {
        const found = findNudge(el.shadowRoot, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  const injectStyle = (nudge) => {
    const root = nudge && nudge.getRootNode();
    if (!root || !(root instanceof ShadowRoot)) return false;
    if (root.getElementById(STYLE_ID)) return true;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.activator-nudge{transform:translateY(calc(-1 * var(--me-chat-lift, 0px)));transition:transform .3s ease-in-out}' +
      '@media (prefers-reduced-motion: reduce){.activator-nudge{transition:none}}';
    root.appendChild(style);
    return true;
  };

  const barShown = () => {
    if (!bar || !bar.classList.contains('sticky-add-to-cart--visible')) return false;
    const cs = getComputedStyle(bar);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    // Barre placée en bas de l'écran (réglage « afficher en haut » désactivé). Sur ordinateur, Booster
    // en fait une carte flottante à quelques pixels du bas : on regarde sa position, pas « bottom: 0 ».
    const top = parseFloat(cs.top);
    return cs.top === 'auto' || Number.isNaN(top) || top > window.innerHeight / 2;
  };

  const apply = () => {
    timer = null;
    const nudge = chat && findNudge(chat.shadowRoot);
    if (!nudge || !injectStyle(nudge)) return;
    let next = 0;
    if (barShown()) {
      // Distance entre le bas du bouton (sans décalage) et le bas de l'écran : on lit la position
      // définie par Shopify (bottom), insensible à l'animation en cours ; repli sur la mesure.
      const cssBottom = parseFloat(getComputedStyle(nudge).bottom);
      const baseOffset = Number.isNaN(cssBottom)
        ? window.innerHeight - (nudge.getBoundingClientRect().bottom + lift)
        : cssBottom;
      // Hauteur occupée par la barre depuis le bas de l'écran (écart sous la carte flottante compris)
      const barBottom = parseFloat(getComputedStyle(bar).bottom) || 0;
      next = Math.max(0, Math.round(barBottom + bar.offsetHeight + GAP - baseOffset));
    }
    if (next !== lift) {
      lift = next;
      chat.style.setProperty('--me-chat-lift', `${lift}px`);
    }
    // Shopify peut reconstruire le widget de chat après coup (nouveau bouton sans notre règle) :
    // tant que la barre est affichée, on revérifie régulièrement (sans bouger si tout est en place).
    if (next > 0 && !watchdog) {
      watchdog = setInterval(() => {
        if (!barShown()) { clearInterval(watchdog); watchdog = null; schedule(); return; }
        apply();
      }, 600);
    } else if (next === 0 && watchdog) {
      clearInterval(watchdog);
      watchdog = null;
    }
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(apply, SETTLE);
  };

  const start = () => {
    chat = document.querySelector('shopify-chat');
    bar = document.querySelector('.sticky-add-to-cart');
    if (!chat || !bar) return false;
    new MutationObserver(schedule).observe(bar, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', schedule);
    // Le bouton du chat peut être (re)créé après coup : on réapplique quand le widget change
    new MutationObserver(schedule).observe(chat.shadowRoot || chat, { childList: true, subtree: true });
    schedule();
    return true;
  };

  if (!start()) {
    // Le widget de chat est injecté par l'application après le chargement de la page
    const observer = new MutationObserver(() => { if (start()) observer.disconnect(); });
    observer.observe(document.body, { childList: true });
    setTimeout(() => observer.disconnect(), 30000);
  }
})();

/* Barre d'achat collante (sections/sticky-add-to-cart) + packs Kaching Bundles (Duo, Trio…).
   Quand un pack est choisi, la barre affiche son prix, son prix barré, son titre et sa quantité.
   Les éléments de Booster ne sont jamais modifiés : ils sont masqués et remplacés par des éléments « pack »
   ajoutés à côté (classe .me-pack-active sur la barre). Revenir au produit seul retire simplement la classe.
   L'ajout au panier n'est pas modifié : le bouton de la barre déclenche le bouton principal, géré par Kaching. */
(() => {
  const lang = (document.documentElement.lang || 'fr').slice(0, 2);
  const REGULAR = { fr: 'Prix normal', en: 'Regular price', de: 'Normalpreis' }[lang] || 'Prix normal';
  const CSS =
    '.sticky-add-to-cart .me-sticky-pack{display:none}' +
    '.sticky-add-to-cart.me-pack-active [data-sticky-price]>[ref="priceContainer"],' +
    '.sticky-add-to-cart.me-pack-active [data-sticky-variants],' +
    '.sticky-add-to-cart.me-pack-active [data-quantity-display]{display:none!important}' +
    '.sticky-add-to-cart.me-pack-active .me-sticky-pack-price{display:flex;flex-wrap:wrap;align-items:baseline;column-gap:.4em}' +
    '.sticky-add-to-cart.me-pack-active .me-sticky-pack-label{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden;white-space:normal;word-break:normal;overflow-wrap:normal;max-width:100%}' +
    '.sticky-add-to-cart.me-pack-active .me-sticky-pack-qty{display:inline}';

  const start = () => {
    const sticky = document.querySelector('.sticky-add-to-cart');
    const block = document.querySelector('.kaching-bundles');
    if (!sticky || !block || !block.querySelector('.kaching-bundles__bar')) return false;

    if (!document.getElementById('me-sticky-pack-style')) {
      const style = document.createElement('style');
      style.id = 'me-sticky-pack-style';
      style.textContent = CSS;
      document.head.append(style);
    }

    const qtyByBar = {};
    try {
      const cfg = JSON.parse(document.querySelector('script.kaching-bundles-deal-block-settings').textContent);
      (cfg.dealBars || []).forEach((b) => { qtyByBar[b.id] = Number(b.quantity) || 1; });
    } catch (e) { /* configuration absente : quantité lue dans le titre « (X2) » */ }

    const text = (root, sel) => {
      const el = root && root.querySelector(sel);
      return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
    };
    const make = (tag, cls, txt) => {
      const el = document.createElement(tag);
      if (cls) el.className = cls;
      if (txt !== undefined) el.textContent = txt;
      return el;
    };

    // Crée (ou recrée si Booster a réécrit la zone) les éléments « pack »
    const ensure = () => {
      const priceEl = sticky.querySelector('[data-sticky-price]');
      const variants = sticky.querySelector('[data-sticky-variants]');
      const qtyDisplay = sticky.querySelector('[data-quantity-display]');
      let price = sticky.querySelector('.me-sticky-pack-price');
      if (priceEl && !priceEl.contains(price)) {
        price = make('div', 'me-sticky-pack me-sticky-pack-price');
        priceEl.append(price);
      }
      let label = sticky.querySelector('.me-sticky-pack-label');
      if (variants && !label) {
        label = make('div', `me-sticky-pack me-sticky-pack-label ${variants.className}`);
        variants.after(label);
      }
      let qty = sticky.querySelector('.me-sticky-pack-qty');
      if (qtyDisplay && !qty) {
        qty = make('span', 'me-sticky-pack me-sticky-pack-qty');
        qtyDisplay.after(qty);
      }
      return { price, label, qty };
    };

    const render = () => {
      const bar = block.querySelector('.kaching-bundles__bar--selected');
      const title = text(bar, '.kaching-bundles__bar-title');
      const fromTitle = Number((title.match(/x\s*(\d+)/i) || [])[1]) || 1;
      const qty = bar ? qtyByBar[bar.dataset.dealBarId] || fromTitle : 1;
      if (qty <= 1) {
        sticky.classList.remove('me-pack-active');
        return;
      }
      const els = ensure();
      const now = text(bar, '.kaching-bundles__bar-price');
      const full = text(bar, '.kaching-bundles__bar-full-price');
      if (els.price) {
        const nowEl = make('span', 'price');
        nowEl.append(make('span', 'money', now));
        els.price.replaceChildren(nowEl);
        if (full && full !== now) {
          const group = make('span');
          group.setAttribute('role', 'group');
          const was = make('span', 'compare-at-price');
          was.append(make('span', 'money', full));
          group.append(make('span', 'visually-hidden', `${REGULAR} `), was);
          els.price.append(group);
        }
      }
      if (els.label) els.label.textContent = title;
      if (els.qty) els.qty.textContent = `\u00a0(${qty})`;
      sticky.classList.add('me-pack-active');
    };

    let timer = null;
    const schedule = () => { clearTimeout(timer); timer = setTimeout(render, 30); };

    block.addEventListener('change', schedule);
    block.addEventListener('click', schedule);
    new MutationObserver(schedule).observe(block, { attributes: true, attributeFilter: ['class'], subtree: true });
    // Booster peut réécrire la barre (variante, ajout au panier) : on vérifie que les éléments « pack » existent toujours
    new MutationObserver(() => {
      if (sticky.classList.contains('me-pack-active') && !sticky.querySelector('.me-sticky-pack-price')) schedule();
    }).observe(sticky, { childList: true, subtree: true });

    schedule();
    return true;
  };

  if (!start()) {
    // Le bloc Kaching est rendu par l'application après le chargement de la page
    const observer = new MutationObserver(() => { if (start()) observer.disconnect(); });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 30000);
  }
})();
